<template>
  <div>
    <section>
      <page-sub-header title="Track" class="mt-4">
        <template #header>
          <div class="d-flex flex-row align-items-center justify-content-end">
            <app-input
              v-model="timeSeries"
              :options="TIME_SERIES_VALUES"
              type="radio"
              buttons
              button-variant="outline-primary"
              class="text-uppercase mb-0"
              size="sm"
            />
            <export-button
              v-if="isDataReady"
              :file-name="`track-metrics-${timeSeries}`"
              :export-data="exportData"
              :export-file-rows="exportFileRows"
              :export-headers="exportHeaders"
              class="btn mt-0"
            />
          </div>
        </template>
      </page-sub-header>
      <sme-card v-if="loading">
        <app-loading />
      </sme-card>
      <template v-else>
        <sme-card v-if="timeGroupedCountDataset.values.length">
          <b-row class="align-items-center mb-4">
            <b-col lg="8" class="mb-3 mb-lg-0">
              <line-chart
                :labels="timeGroupDataset"
                :datasets="timeGroupedCountDataset"
                :chart-options="{
                  aspectRatio: getInitialAspectRatio(3, 2),
                  scales: { y: { ticks: { precision: 0 }, title: { text: timeGroupedCountDataset.name } } },
                }"
                fill-gradient
              />
            </b-col>
            <b-col lg="4">
              <app-stat
                :stat="lastCountValue"
                :previous-stat="prevCountValue"
                :formatter="toCompactNumber"
                description="Employees Viewing Track"
                class="mb-3"
                background-color="core-1-lighten-90"
                color="default"
              />
              <p class="mb-0">
                {{ timeGroupTitle }}, {{ toCompactNumber(lastCountValue) }} of your enrolled employees viewed track.
                {{ countPrevDiffText }}
              </p>
            </b-col>
          </b-row>

          <b-row class="align-items-center">
            <b-col lg="8" class="mb-3 mb-lg-0">
              <line-chart
                :labels="timeGroupDataset"
                :datasets="timeGroupedPercentageDataset"
                :chart-options="{
                  aspectRatio: getInitialAspectRatio(3, 2),
                  scales: { y: { title: { text: timeGroupedPercentageDataset.name } } },
                }"
                fill-gradient
              />
            </b-col>
            <b-col lg="4">
              <app-stat
                :stat="lastPercentageValue"
                :previous-stat="prevPercentage"
                :formatter="toNumber"
                suffix="%"
                description="Track Percentage"
                class="mb-3"
                background-color="core-1-lighten-90"
                color="default"
              />
              <p class="mb-0">
                {{ timeGroupTitle }}, {{ lastPercentageValue }}% of your enrolled employees viewed track.
                {{ percentagePrevDiffText }}
              </p>
            </b-col>
          </b-row>
        </sme-card>
        <sme-card v-else>
          <p class="my-2 text-center">No data to display.</p>
        </sme-card>
      </template>
    </section>

    <!-- Budget Section -->
    <section v-if="portalUpdatesJan2026Enabled">
      <page-sub-header title="Budget" class="mt-4">
        <template #header>
          <div class="d-flex flex-row align-items-center justify-content-end">
            <app-input
              v-model="budgetTimeSeries"
              :options="TIME_SERIES_VALUES"
              type="radio"
              buttons
              button-variant="outline-primary"
              class="text-uppercase mb-0"
              size="sm"
            />
            <export-button
              v-if="isBudgetDataReady"
              :file-name="`budget-metrics-${budgetTimeSeries}`"
              :export-data="budgetExportData"
              :export-file-rows="budgetExportFileRows"
              :export-headers="budgetExportHeaders"
              class="btn mt-0"
            />
          </div>
        </template>
      </page-sub-header>
      <sme-card v-if="budgetLoading">
        <app-loading />
      </sme-card>
      <template v-else>
        <sme-card v-if="timeGroupedBudgetCountDataset?.values?.length">
          <b-row class="align-items-center mb-4">
            <b-col lg="8" class="mb-3 mb-lg-0">
              <line-chart
                :labels="budgetTimeGroupDataset"
                :datasets="timeGroupedBudgetCountDataset"
                :chart-options="{
                  aspectRatio: getInitialAspectRatio(3, 2),
                  scales: { y: { ticks: { precision: 0 }, title: { text: timeGroupedBudgetCountDataset.name } } },
                }"
                fill-gradient
              />
            </b-col>
            <b-col lg="4">
              <app-stat
                :stat="lastBudgetCountValue"
                :previous-stat="prevBudgetCountValue"
                :formatter="toCompactNumber"
                description="Employees Using Budget"
                class="mb-3"
                background-color="core-1-lighten-90"
                color="default"
              />
              <p class="mb-0">
                {{ budgetTimeGroupTitle }}, {{ toCompactNumber(lastBudgetCountValue) }} of your enrolled employees used
                Budget. {{ budgetCountPrevDiffText }}
              </p>
            </b-col>
          </b-row>

          <b-row class="align-items-center">
            <b-col lg="8" class="mb-3 mb-lg-0">
              <line-chart
                :labels="budgetTimeGroupDataset"
                :datasets="timeGroupedBudgetPercentageDataset"
                :chart-options="{
                  aspectRatio: getInitialAspectRatio(3, 2),
                  scales: { y: { title: { text: timeGroupedBudgetPercentageDataset.name } } },
                }"
                fill-gradient
              />
            </b-col>
            <b-col lg="4">
              <app-stat
                :stat="lastBudgetPercentageValue"
                :previous-stat="prevBudgetPercentage"
                :formatter="toNumber"
                suffix="%"
                description="Budget Percentage"
                class="mb-3"
                background-color="core-1-lighten-90"
                color="default"
              />
              <p class="mb-0">
                {{ budgetTimeGroupTitle }}, {{ lastBudgetPercentageValue }}% of your enrolled employees used Budget.
                {{ budgetPercentagePrevDiffText }}
              </p>
            </b-col>
          </b-row>
        </sme-card>
        <sme-card v-else>
          <p class="my-2 text-center">No Budget data to display.</p>
        </sme-card>
      </template>
    </section>
  </div>
</template>

<script setup>
import { computed, onBeforeMount, ref, watch } from 'vue';
import ApiClient from '@/ApiClient';
import AppInput from '@/components/AppInput.vue';
import AppLoading from '@/components/AppLoading.vue';
import AppStat from '@/components/AppStat.vue';
import PageSubHeader from '@/components/PageSubHeader.vue';
import SmeCard from '@/components/atoms/SmeCard.vue';
import LineChart from '@/components/charts/LineChart.vue';
import useTimeGroupDataset from '@/composables/useTimeGroupDataset';
import useDivisionSelection from '@/state/composables/useDivisionSelection';
import useFeatureFlags from '@/composables/useFeatureFlags';
import { TIME_SERIES_VALUES, getInitialAspectRatio } from '@/utils/chart';
import { toCompactNumber, toNumber } from '@/utils/common';
import ExportButton from '@/components/ExportButton.vue';

const { divisionId } = useDivisionSelection();
const { portalUpdatesJan2026Enabled } = useFeatureFlags();

const loading = ref(true);
const timeDataset = ref(null);
const countDataset = ref(null);
const percentageDataset = ref(null);

const { timeSeries, timeGroupDataset, timeGroupTitle, datasetToTimeGroupedDataset, getPrevDiffText } =
  useTimeGroupDataset(timeDataset);

const timeGroupedCountDataset = computed(() => datasetToTimeGroupedDataset(countDataset));
const timeGroupedPercentageDataset = computed(() => datasetToTimeGroupedDataset(percentageDataset));
const lastCountValue = computed(() => timeGroupedCountDataset.value?.calculated.last);
const prevCountValue = computed(() => timeGroupedCountDataset.value?.calculated.prev);
const lastPercentageValue = computed(() => timeGroupedPercentageDataset.value?.calculated.last);
const prevPercentage = computed(() => timeGroupedPercentageDataset.value?.calculated.prev);
const countPrevDiffText = computed(() => getPrevDiffText(timeGroupedCountDataset));
const percentagePrevDiffText = computed(() =>
  getPrevDiffText(timeGroupedPercentageDataset, value => `${Number(toNumber(value))}%`),
);

// Budget users data
const budgetLoading = ref(true);
const budgetTimeDataset = ref(null);
const budgetCountDataset = ref(null);
const budgetPercentageDataset = ref(null);

const {
  timeSeries: budgetTimeSeries,
  timeGroupDataset: budgetTimeGroupDataset,
  timeGroupTitle: budgetTimeGroupTitle,
  datasetToTimeGroupedDataset: budgetDatasetToTimeGroupedDataset,
  getPrevDiffText: budgetGetPrevDiffText,
} = useTimeGroupDataset(budgetTimeDataset);

const timeGroupedBudgetCountDataset = computed(() => budgetDatasetToTimeGroupedDataset(budgetCountDataset));
const timeGroupedBudgetPercentageDataset = computed(() => budgetDatasetToTimeGroupedDataset(budgetPercentageDataset));
const lastBudgetCountValue = computed(() => timeGroupedBudgetCountDataset.value?.calculated.last);
const prevBudgetCountValue = computed(() => timeGroupedBudgetCountDataset.value?.calculated.prev);
const lastBudgetPercentageValue = computed(() => timeGroupedBudgetPercentageDataset.value?.calculated.last);
const prevBudgetPercentage = computed(() => timeGroupedBudgetPercentageDataset.value?.calculated.prev);
const budgetCountPrevDiffText = computed(() => budgetGetPrevDiffText(timeGroupedBudgetCountDataset));
const budgetPercentagePrevDiffText = computed(() =>
  budgetGetPrevDiffText(timeGroupedBudgetPercentageDataset, value => `${Number(toNumber(value))}%`),
);

watch([divisionId, timeSeries], () => getTrackMetrics());

watch([divisionId, budgetTimeSeries], () => {
  if (portalUpdatesJan2026Enabled.value) {
    getBudgetUsersMetrics();
  }
});

const getTrackMetrics = async () => {
  loading.value = true;

  const { results } = await ApiClient.getTrackMetrics(timeSeries.value, divisionId.value);

  if (results?.length) {
    const time = results.find(result => result.id === 'time');
    const track = results.find(result => result.id === 'track_users');
    const enrolled = results.find(result => result.id === 'enrolled');

    timeDataset.value = time;
    countDataset.value = {
      ...track,
      name: 'Number of employees',
    };
    percentageDataset.value = {
      name: 'Percentage (%)',
      values: enrolled.values.map((value, index) =>
        value ? Number(toNumber((track.values[index] / value) * 100)) : 0,
      ),
    };

    loading.value = false;
  }
};

const getBudgetUsersMetrics = async () => {
  budgetLoading.value = true;

  const { results } = await ApiClient.getPortalTrackBudgetUsers(budgetTimeSeries.value, divisionId.value);

  if (results?.length) {
    const time = results.find(result => result.id === 'time');
    const budgetUsers = results.find(result => result.id === 'budget_users');
    const enrolled = results.find(result => result.id === 'enrolled');

    budgetTimeDataset.value = time;
    budgetCountDataset.value = {
      ...budgetUsers,
      name: 'Budget users',
    };
    budgetPercentageDataset.value = {
      name: 'Percentage (%)',
      values: enrolled.values.map((value, index) =>
        value ? Number(toNumber((budgetUsers.values[index] / value) * 100)) : 0,
      ),
    };
  }

  budgetLoading.value = false;
};

onBeforeMount(() => {
  getTrackMetrics();
  if (portalUpdatesJan2026Enabled.value) {
    getBudgetUsersMetrics();
  }
});

const isDataReady = computed(() => {
  return (
    timeGroupDataset.value?.values && countDataset.value?.values && percentageDataset.value?.values && !loading.value
  );
});

const exportData = computed(() => {
  if (!isDataReady.value) {
    return [];
  }

  return timeGroupDataset.value.values.map((time, index) => ({
    time,
    viewers: countDataset.value.values[index],
    percentage: percentageDataset.value.values[index],
  }));
});

const exportHeaders = computed(() => {
  if (!isDataReady.value) {
    return [];
  }
  return ['Date', countDataset.value.name, percentageDataset.value.name];
});

const exportFileRows = item => [item.time, item.viewers, item.percentage];

// Budget export functionality
const isBudgetDataReady = computed(() => {
  return (
    budgetTimeGroupDataset.value?.values &&
    budgetCountDataset.value?.values &&
    budgetPercentageDataset.value?.values &&
    !budgetLoading.value
  );
});

const budgetExportData = computed(() => {
  if (!isBudgetDataReady.value) {
    return [];
  }

  return budgetTimeGroupDataset.value.values.map((time, index) => ({
    time,
    users: budgetCountDataset.value.values[index],
    percentage: budgetPercentageDataset.value.values[index],
  }));
});

const budgetExportHeaders = computed(() => {
  if (!isBudgetDataReady.value) {
    return [];
  }
  return ['Date', budgetCountDataset.value.name, budgetPercentageDataset.value.name];
});

const budgetExportFileRows = item => [item.time, item.users, item.percentage];
</script>
